import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { getSocketBaseUrl, getAuthToken } from '../config/api';
import { featureFlags } from '../config/featureFlags';
import scannerService from '../services/scannerService';

const terminalStatuses = new Set(['completed', 'failed', 'cancelled']);

const normalizePayload = (payload = {}) => {
  const data = payload.data || payload;
  const progress = Number(data.progress_percent ?? data.progress ?? payload.progress_percent ?? payload.progress ?? 0);
  return {
    scanId: payload.scan_id || data.scan_id,
    status: data.scan_status || data.status || payload.event_type || 'running',
    progress: Math.max(0, Math.min(100, Number.isFinite(progress) ? progress : 0)),
    phase: data.current_stage || data.phase || 'starting',
    message: data.status_message || data.message || '',
    summary: data.summary || {},
    vulnerabilities: data.vulnerabilities || data.findings || payload.vulnerabilities || [],
    timestamp: payload.timestamp || data.timestamp || new Date().toISOString(),
  };
};

export const useScanProgress = (scanId, options = {}) => {
  const {
    enabled = true,
    onComplete,
    onError,
    pollIntervalMs = 3000,
  } = options;

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onComplete, onError]);

  const [connectionState, setConnectionState] = useState('idle');
  const [latencyMs, setLatencyMs] = useState(null);
  const [progressState, setProgressState] = useState({
    status: 'loading',
    progress: 0,
    phase: 'loading',
    message: 'Loading scan status...',
    summary: {},
    vulnerabilities: [],
    timestamp: null,
  });
  const [metrics, setMetrics] = useState({
    connectedAt: null,
    reconnects: 0,
    fallbackPolls: 0,
    messagesReceived: 0,
    lastEventLatencyMs: null,
  });
  const socketRef = useRef(null);
  const pollTimerRef = useRef(null);
  const completedRef = useRef(false);

  const closeSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const applyUpdate = useCallback((payload) => {
    const update = normalizePayload(payload);
    const eventTime = update.timestamp ? new Date(update.timestamp).getTime() : null;
    setMetrics((previous) => ({
      ...previous,
      messagesReceived: previous.messagesReceived + 1,
      lastEventLatencyMs: eventTime ? Math.max(0, Date.now() - eventTime) : previous.lastEventLatencyMs,
    }));
    setProgressState((previous) => ({
      ...previous,
      ...update,
      progress: Math.max(previous.progress || 0, update.progress || 0),
      vulnerabilities: update.vulnerabilities || previous.vulnerabilities || [],
    }));

    if (terminalStatuses.has(String(update.status || '').toLowerCase()) && !completedRef.current) {
      completedRef.current = true;
      stopPolling();
      if (String(update.status).toLowerCase() === 'completed') {
        onCompleteRef.current?.(update);
      } else {
        onErrorRef.current?.(update);
      }
    }
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    if (!enabled || !scanId || pollTimerRef.current) {
      return;
    }

    setConnectionState((state) => (state === 'connected' ? state : 'polling'));
    const poll = async () => {
      try {
        const status = await scannerService.getScanStatus(scanId);
        setMetrics((previous) => ({ ...previous, fallbackPolls: previous.fallbackPolls + 1 }));
        applyUpdate(status);
      } catch (error) {
        setConnectionState('polling-error');
        onErrorRef.current?.(error);
      }
    };

    poll();
    pollTimerRef.current = setInterval(poll, pollIntervalMs);
  }, [applyUpdate, enabled, pollIntervalMs, scanId]);

  useEffect(() => {
    if (!enabled || !scanId) {
      return undefined;
    }

    completedRef.current = false;
    if (!featureFlags.realtimeScanUpdates) {
      setConnectionState('feature-disabled-polling');
      startPolling();
      return () => stopPolling();
    }

    setConnectionState('connecting');
    const token = getAuthToken();
    const socketBaseUrl = getSocketBaseUrl(featureFlags.useBffSocket);
    const socket = io(socketBaseUrl, {
      auth: token ? { token } : {},
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 8000,
    });
    socketRef.current = socket;

    const handleScanUpdate = (payload) => applyUpdate(payload);

    socket.on('connect', () => {
      setConnectionState('connected');
      setMetrics((previous) => ({ ...previous, connectedAt: Date.now() }));
      socket.emit('join_scan', { scan_id: scanId });
      socket.emit('heartbeat', (response) => {
        if (response?.timestamp) {
          setLatencyMs(Math.max(0, Date.now() - new Date(response.timestamp).getTime()));
        }
      });
      stopPolling(); // Stop polling when websocket is successfully connected
    });
    socket.on('connect_error', () => {
      setConnectionState('fallback-polling');
      startPolling();
    });
    socket.on('disconnect', () => {
      if (!completedRef.current) {
        setConnectionState('reconnecting');
        setMetrics((previous) => ({ ...previous, reconnects: previous.reconnects + 1 }));
        startPolling();
      }
    });
    socket.on('reconnect_failed', () => {
      setConnectionState('fallback-polling');
      startPolling();
    });

    ['scan:started', 'scan:progress', 'scan:analyzing', 'scan:vulnerability_found', 'scan:completed', 'scan:failed', 'scan:cancelled'].forEach((eventName) => {
      socket.on(eventName, handleScanUpdate);
    });

    startPolling();

    return () => {
      stopPolling();
      if (socket.connected) {
        socket.emit('leave_scan', { scan_id: scanId });
      }
      closeSocket();
    };
  }, [applyUpdate, closeSocket, enabled, scanId, startPolling, stopPolling]);

  const retry = useCallback(() => {
    completedRef.current = false;
    stopPolling();
    if (socketRef.current) {
      setConnectionState('connecting');
      socketRef.current.connect();
    } else {
      startPolling();
    }
  }, [startPolling, stopPolling]);

  return useMemo(() => ({
    ...progressState,
    connectionState,
    latencyMs,
    metrics,
    isConnected: connectionState === 'connected',
    isFallback: connectionState.includes('polling'),
    isComplete: terminalStatuses.has(String(progressState.status || '').toLowerCase()),
    retry,
  }), [connectionState, latencyMs, progressState, retry]);
};

export default useScanProgress;
